import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';

/**
 * Base class for any Angular service that touches Firebase APIs
 * (Firestore, Storage, Functions callables, …).
 *
 * AngularFire requires every Firebase call to run inside Angular's DI
 * scope. The scope is established when Angular instantiates a class
 * (constructor, `inject()`, lifecycle hooks) but **lost** as soon as
 * execution enters an asynchronous boundary — RxJS `switchMap`,
 * Promise `.then`, `setTimeout`, etc.
 *
 * Calling Firebase APIs outside the scope triggers the
 * `Calling Firebase APIs outside of an Injection context` warning,
 * may cause change-detection skips, and may break outright on future
 * AngularFire releases.
 *
 * This base captures the injector once at construction and exposes
 * `inCtx(fn)` to re-enter the scope around every Firebase call. The
 * pattern is consistent across every service in this codebase — see
 * [.doc/firebase-injection-context.md](../../../../.doc/firebase-injection-context.md).
 *
 * @example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class ThingService extends FirebaseAwareService {
 *   private firestore = inject(Firestore);
 *
 *   async get(id: string): Promise<Thing | null> {
 *     const snap = await this.inCtx(() =>
 *       getDoc(doc(this.firestore, 'things', id))
 *     );
 *     return snap.exists() ? (snap.data() as Thing) : null;
 *   }
 * }
 * ```
 *
 * **Rule of thumb**: every method that calls a Firebase API wraps it in
 * `this.inCtx(() => ...)`. If a method awaits between two Firebase calls,
 * **each** call needs its own wrap (the scope is lost after every await).
 */
@Injectable()
export abstract class FirebaseAwareService {
  /**
   * Captured at construction time, when Angular's DI scope is active.
   * Used to re-enter that scope from async callbacks.
   */
  protected readonly injector: Injector = inject(Injector);

  /**
   * Runs `fn` inside the service's DI scope so AngularFire calls inside
   * it have the context they require. Returns whatever `fn` returns.
   *
   * Wrap every Firebase API call (`doc`, `getDoc`, `query`, `collection`,
   * `where`, `addDoc`, `setDoc`, `updateDoc`, `runTransaction`,
   * `onSnapshot`, `httpsCallable`, `ref`, `uploadBytes`,
   * `getDownloadURL`, …) in this helper.
   */
  protected inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }
}
